import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  BellAlertIcon,
  BoltIcon,
  Box,
  Button,
  DebouncedTextField,
  DeleteConfirmButton,
  Flex,
  IconButton,
  MagnifyingGlassIcon,
  Spacer,
  Spinner,
  Table,
  TableProps,
  Text,
  toaster,
  Tooltip,
} from 'components'
import { TableAPI } from 'components/Table'
import { SortDirection, SortingMode } from 'ka-table'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Lead, Sort } from 'types'
import {
  deleteLeadSessions,
  forceLight,
  getLead,
  sleep,
  updateLeads,
  useBreakpoint,
  usePermission,
  useTheme,
} from 'utils'
import { defaultProps } from 'utils/tableUtils'
import { SessionExportButton } from '..'
import { EmptyState, FilterSelector } from '../..'
import noLeads from '../../../assets/emptyStates/no_leads.svg'
import { useEventInfo } from '../../../pages/EventInfoPage'
import {
  flattenSession,
  LeadTableColumn,
  makeColumns,
  mobileDefSessionFields,
  useEvent,
  useLeadSessions,
  useNewSessionCount,
} from '../../../utils'
import { useAggregations } from '../../../utils/analyticsUtils'
import GiveawayButton from '../GiveawayButton'
import SessionDrawer from '../SessionDrawer'
import SessionImportButton from '../SessionImportButton'
import LeadTableColumnSelector from './LeadTableColumnSelector'

interface Props {
  searchText?: string
  embeddedTable?: boolean
}

export default function EventLeads({ searchText, embeddedTable }: Props) {
  const { shrinkBanner, updateShrinkBanner } = useEventInfo()
  const { event, filters } = useEvent()
  const permission = usePermission()
  const isMobile = useBreakpoint() < 2
  const newSessionCount = useNewSessionCount()

  const [tableProps, setTableProps] = useState(initialTableProps)
  const [search, setSearch] = useState(searchText || '')
  const [showNewSessionButton, setShowNewSessionButton] = useState(false)
  const { theme } = useTheme()
  const [selectedId, setSelectedId] = useState<string>('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [page, setPage] = useState(0)
  const [columns, setColumns] = useState<LeadTableColumn[]>([])
  const [defaultColumns, setDefaultColumns] = useState<LeadTableColumn[]>([])
  const [sort, setSort] = useState<Sort>({ by: 'created', order: 'DESC' })
  const sessionProps = { limit: pageSize, offset: page * pageSize, search, sort }
  const [sessions, sessionsStatus, hasAllSessions, updateSessions, removeSessions] =
    useLeadSessions(sessionProps)
  const sessionsRef = useRef(sessions)
  sessionsRef.current = sessions
  const tableAPI = useRef<TableAPI>(null)
  const isAdmin = permission.event === 'admin'
  const ExpandIcon = shrinkBanner ? ArrowsPointingInIcon : ArrowsPointingOutIcon
  const selectedSession = sessions.find(s => s.id === selectedId)
  const sessionCount = useFilteredSessionCount()
  const enableColumnReset = columns.some(c => {
    const defaultColumn = defaultColumns.find(d => d.key === c.key)
    return Boolean(defaultColumn?.visible) !== Boolean(c.visible)
  })

  const updateSelectedRows = (newSelectedRows: string[]) => {
    setSelectedIds(newSelectedRows)
  }

  useEffect(() => {
    if (!event) return

    makeColumns({
      workspaceId: event.workspaceId,
      event,
      onColumnUpdate: isAdmin ? handleLeadUpdate : undefined,
    }).then(columns => {
      setColumns(columns)
      setDefaultColumns(columns)
    })
  }, [event?.id, isAdmin])

  useEffect(() => {
    resetTable()
  }, [filters])

  const data = useMemo(() => {
    return sessions.map(s => flattenSession(s, event!))
  }, [sessions])

  const columnProps = useMemo(() => {
    const withSort = columns.map(c => {
      const sortDirection =
        c.key === sort.by
          ? sort.order === 'DESC'
            ? SortDirection.Descend
            : SortDirection.Ascend
          : undefined
      return { ...c, sortDirection }
    })

    if (!isMobile) return { columns: withSort }

    const mobileColumns = columns.map(c => ({
      ...c,
      visible: mobileDefSessionFields.includes(c.key),
      width: c.key === 'temp' ? 70 : c.width,
    }))
    return { columns: mobileColumns, pinnedColumns: ['name'] }
  }, [columns, sort, isMobile])

  const preventFetch = useRef(false)

  async function handleScrollToBottom() {
    if (preventFetch.current || hasAllSessions) return

    setPage(s => s + 1)
    preventFetch.current = true
    await sleep(500)
    preventFetch.current = false
  }

  function handleNewSessionButton() {
    setTableProps(s => {
      const columns = s.columns.map(c => {
        const sortDirection = c.key === 'created' ? SortDirection.Descend : undefined
        return { ...c, sortDirection }
      })
      return { ...s, columns }
    })
    setSearch('')
    setShowNewSessionButton(false)
  }

  const handleTableChange: TableProps['onChange'] = (props, action) => {
    switch (action.type) {
      case 'UpdateSortDirection': {
        const { columnKey } = action
        let direction: Sort['order'] = 'ASC'
        if (columnKey === sort.by) direction = sort.order === 'ASC' ? 'DESC' : 'ASC'
        else if (columnKey === 'created') direction = 'DESC'
        if (columnKey === 'name') setSort({ by: 'firstName', order: direction })
        else setSort({ by: columnKey, order: direction })
        resetTable()
        break
      }
      case 'ReorderColumns': {
        const { columnKey, targetColumnKey } = action
        const index = columns.findIndex(c => c.key === columnKey)
        const targetIndex = columns.findIndex(c => c.key === targetColumnKey)
        const newColumns = [...columns]
        newColumns.splice(index, 1)
        newColumns.splice(targetIndex, 0, columns[index])
        setColumns(newColumns)
        break
      }
      case 'ResizeColumn': {
        const { columnKey, width } = action
        const newColumns = columns.map(c => (c.key === columnKey ? { ...c, width } : c))
        setColumns(newColumns)
        break
      }
      default: {
        setTableProps(props)
      }
    }
  }

  async function handleLeadUpdate(updatedLead?: Partial<Lead> & { id: string }) {
    setSelectedId('')
    if (!updatedLead) return

    function update(lead: Lead & { id: string }) {
      const impactedSessions = sessionsRef.current.filter(s => s.lead?.id === lead.id)
      updateSessions(impactedSessions.map(s => ({ ...s, lead: { ...s.lead!, ...lead } })))
    }

    await updateLeads({ updates: [updatedLead] })
    update(updatedLead)

    await sleep(1000) // wait for augmentation to complete

    const lead = (await getLead({ leadId: updatedLead.id })).data
    update(lead)
  }

  async function handleDelete() {
    if (!event) return

    const { id: eventId, workspaceId } = event
    removeSessions(selectedIds)
    updateSelectedRows?.([])

    try {
      await deleteLeadSessions({
        deleteOrphanedLeads: true,
        eventId,
        sessionIds: selectedIds,
        workspaceId,
      })
    } catch (e) {
      console.error('Error deleting leads and sessions:', e)
      toaster.create({
        title: 'Error deleting sessions',
        description: 'Something went wrong. Refresh the page and try again.',
        type: 'error',
      })
    }
  }

  function resetTable() {
    setPage(0)
    tableAPI.current?.scrollToTop()
  }

  return (
    <>
      <Flex direction="column" overflow="hidden" mx={{ base: 1, md: 0 }} gap={3} minH="100%">
        <Flex gap={2} p={1}>
          <DebouncedTextField
            width={{ base: '100%', sm: '250px' }}
            onChange={v => {
              setSearch(v)
              resetTable()
            }}
            leftElement={<MagnifyingGlassIcon />}
            placeholder="Search..."
            value={search}
            controlled
            boxShadow="0px 1px 4px 0px rgba(0, 0, 0, 0.06)"
            backdropFilter="blur(14px)"
            bg={theme === 'light' ? 'rgba(255, 255, 255, 0.74)' : 'rgba(255, 255, 255, 0.14)'}
            borderRadius="10px"
            borderWidth={0}
            pl={10}
          />
          <Flex flex={1} justifyContent="space-between" gap={2} align="center">
            <Box>
              {showNewSessionButton && (
                <Button
                  palette="primary"
                  leftIcon={<BellAlertIcon type="outline" />}
                  onClick={handleNewSessionButton}
                >
                  New session!
                </Button>
              )}
            </Box>
            <Flex gap={2} hideBelow="md">
              <FilterSelector columns={columns} />
              <GiveawayButton />
              <LeadTableColumnSelector
                columns={columns}
                onChange={setColumns}
                onReset={enableColumnReset ? () => setColumns(defaultColumns) : undefined}
              />
              {!embeddedTable && <SessionImportButton updateSessions={updateSessions} />}
              <SessionExportButton />
            </Flex>
          </Flex>
        </Flex>

        {sessionCount > 0 ? (
          <Box display="flex" flexDir="column" overflowY="auto" borderRadius="14px">
            <Box
              {...forceLight}
              display="flex"
              alignItems="center"
              justifyContent={selectedIds.length > 0 ? 'flex-start' : 'center'}
              h="47px"
              pl={4}
              gap={2}
              borderTopRadius="14px"
              borderBottomWidth="1px solid rgba(203, 203, 203, 0.4)"
              overflow="hidden"
              backgroundColor="white"
              flexShrink={0}
            >
              <BoltIcon fontSize="md" color="primary.500" />
              {selectedIds.length > 0 ? (
                <Flex gap={2} align={'center'}>
                  <Text
                    textStyle={'bodySmall'}
                    borderRightWidth={'2px solid rgba(23, 23, 23, 0.2)'}
                    pr={3}
                  >
                    {selectedIds.length} of {sessionCount.toLocaleString()} Sessions Selected
                  </Text>
                  {isAdmin && (
                    <DeleteConfirmButton
                      onDelete={handleDelete}
                      disabled={selectedIds.length === 0}
                      description={`Type "Delete" if you are certain you want to delete the selected ${
                        selectedIds.length > 1 ? 'sessions' : 'session'
                      }`}
                      textStyle="bodySmall"
                    />
                  )}
                </Flex>
              ) : sessionCount !== event?.sessionCount ? (
                <Text textStyle="bodySmall">
                  Filtered Sessions: {sessionCount?.toLocaleString()} of{' '}
                  {event?.sessionCount.toLocaleString()}
                </Text>
              ) : (
                <Text textStyle="bodySmall">
                  Total Sessions: {sessionCount.toLocaleString() || '--'}
                </Text>
              )}

              {newSessionCount > 0 && (
                <Flex align="center" color="primary.500" gap={1}>
                  <BellAlertIcon type="outline" size="sm" />
                  <Text textStyle="bodySmall">+{newSessionCount} New</Text>
                </Flex>
              )}
              <Spacer />
              {!embeddedTable && (
                <Tooltip
                  content={`${shrinkBanner ? 'Collapse' : 'Expand'} table`}
                  positioning={{ placement: 'top' }}
                >
                  <IconButton
                    aria-label="expand table"
                    onClick={() => updateShrinkBanner(!shrinkBanner)}
                    icon={<ExpandIcon size="sm" />}
                    variant="ghost"
                  />
                </Tooltip>
              )}
            </Box>

            <Table
              ref={tableAPI}
              {...tableProps}
              {...columnProps}
              data={data}
              onChange={handleTableChange}
              loading={{ enabled: sessionsStatus === 'loading' }}
              onRowClick={setSelectedId}
              onScrollToBottom={handleScrollToBottom}
              onSelectedRowsChange={isMobile ? undefined : updateSelectedRows}
              searchText={search}
              selectedRows={isMobile || !isAdmin ? undefined : selectedIds}
              sortingMode={hasAllSessions ? SortingMode.Single : SortingMode.SingleRemote}
              emptyText="No sessions"
            />
          </Box>
        ) : filters?.length ? (
          sessionsStatus === 'loading' ? (
            <Spinner />
          ) : (
            <EmptyState title="No data retrieved, change filters and try again" />
          )
        ) : (
          <Box
            {...forceLight}
            borderRadius="14px"
            backgroundColor="white"
            display="flex"
            justifyContent="center"
            p={10}
          >
            <EmptyState
              iconSrc={noLeads}
              title="No Leads"
              description="Once an event begins, leads captured will be available here to view and manage."
            />
          </Box>
        )}
      </Flex>

      <SessionDrawer session={selectedSession} onDone={handleLeadUpdate} />
    </>
  )
}

const pageSize = 40

const initialTableProps: TableProps = {
  ...defaultProps,
  columnReordering: true,
  pinnedColumns: ['temp', 'name'],
}

function useFilteredSessionCount() {
  const { filters, event } = useEvent()

  const eventDCFilters = filters || []
  const { aggregations } = useAggregations([
    {
      filters: eventDCFilters,
      table: 'leadSessions',
      values: ['_count'],
    },
  ])
  const [sessionsCount] = aggregations || {}
  return eventDCFilters.length
    ? (sessionsCount?.data?.[0]?._count as number)
    : event?.sessionCount || 0
}
